// src/services/entityService.js
import api from '@/core/api/api';

const entityService = {
  // Create entity (firm or institution)
  createEntity: async (entityType, entityData, logoTempLocation = null, selectedLocationIndex = null, regulatoryData = null) => {
    try {
      const payload = {
        entity_type: entityType,
        entity_data: entityData
      };
      
      if (logoTempLocation) payload.logo_temp_location = logoTempLocation;
      if (selectedLocationIndex !== null) payload.selected_location_index = selectedLocationIndex;
      
      // NEW: Handle both regulatory registrations array and legacy regulatory bodies dict
      if (regulatoryData) {
        if (Array.isArray(regulatoryData)) {
          // New format: regulatory registrations array
          payload.regulatory_registrations = regulatoryData;
        } else {
          // Legacy format: regulatory bodies dictionary
          payload.regulatory_bodies = regulatoryData;
        }
      }
      
      const response = await api.post('/entities/maintain', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating entity:', error);
      throw error;
    }
  },

  // Update entity
  updateEntity: async (entityType, entityId, entityData, logoTempLocation = null, selectedLocationIndex = null, regulatoryData = null) => {
    try {
      const payload = {
        entity_type: entityType,
        entity_id: entityId,
        entity_data: entityData
      };
      
      if (logoTempLocation) payload.logo_temp_location = logoTempLocation;
      if (selectedLocationIndex !== null) payload.selected_location_index = selectedLocationIndex;
      
      // LEGACY: Handle separate regulatory data parameter for backward compatibility
      if (regulatoryData) {
        if (Array.isArray(regulatoryData)) {
          // New format: regulatory registrations array
          payload.regulatory_registrations = regulatoryData;
        } else {
          // Legacy format: regulatory bodies dictionary
          payload.regulatory_bodies = regulatoryData;
        }
      }
      
      const response = await api.put('/entities/maintain', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating entity:', error);
      throw error;
    }
  },

  // Get entity by ID
  getEntityById: async (entityType, entityId) => {
    try {
      const response = await api.get('/entities/maintain', {
        params: { entity_type: entityType, entity_id: entityId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching entity by ID:', error);
      throw error;
    }
  },

  // Get entity by manager ID (for firms)
  getEntityByManagerId: async (managerId) => {
    try {
      const response = await api.get('/entities/maintain', {
        params: { entity_type: 'firm', manager_id: managerId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching entity by manager ID:', error);
      throw error;
    }
  },

  // Get entity by investor ID (for institutions)
  getEntityByInvestorId: async (investorId) => {
    try {
      console.log('🌐 EntityService: Making API call to /entities/maintain with:', { entity_type: 'institution', investor_id: investorId });
      const response = await api.get('/entities/maintain', {
        params: { entity_type: 'institution', investor_id: investorId }
      });
      console.log('🌐 EntityService: API response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('🚨 EntityService Error fetching entity by investor ID:', error);
      throw error;
    }
  },

  // Get location suggestions
  getLocationSuggestions: async (entityName, additionalContext = {}) => {
    try {
      const response = await api.post('/entities/maintain/check', {
        entity_name: entityName,
        ...additionalContext
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      throw error;
    }
  },

  // Helper method to get firm details (backward compatibility)
  getFirmDetails: async (firmId) => {
    const response = await entityService.getEntityById('firm', firmId);
    return { firm: response.entity };
  },

  // Helper method to get firm by manager ID (backward compatibility)
  getFirmDetailsByManagerId: async (managerId) => {
    const response = await entityService.getEntityByManagerId(managerId);
    return { firm: response.entity };
  },

  // Helper method to update firm (backward compatibility)
  updateFirmDetails: async (firmId, firmData, logoTempLocation = null, selectedLocationIndex = null, regulatoryBodies = null) => {
    const response = await entityService.updateEntity('firm', firmId, firmData, logoTempLocation, selectedLocationIndex, regulatoryBodies);
    return { firm: response.entity };
  },

  // Helper method for location checking (backward compatibility)
  checkFirmName: async (data) => {
    const response = await entityService.getLocationSuggestions(data.firm_name || data.firmName, {
      firm_city: data.firm_city || data.firmCity,
      firm_country: data.firm_country || data.firmCountry
    });
    return {
      location_suggestions: response.location_suggestions,
      existing_firms: [] // No longer checking for duplicates
    };
  },

  // === FIRM MANAGEMENT ===

  /**
   * Tier A wizard preview: POST /entities/firms { enrich_preview, firm_name }
   * Returns { firm_fields, sources_used } from enrich Lambda (text-only).
   */
  enrichFirmPreview: async (firmName) => {
    try {
      const response = await api.post('/entities/firms', {
        enrich_preview: true,
        firm_name: firmName,
      });
      return response.data;
    } catch (error) {
      console.error('Error enrichFirmPreview:', error);
      throw error;
    }
  },

  /**
   * Create new firm (POST /entities/firms)
   */
  createFirm: async (firmData, createdBy, logoTempLocation = null, regulatoryBodies = null, billingData = null) => {
    try {
      const payload = {
        firm_data: firmData,
        created_by: createdBy
      };
      
      if (logoTempLocation) payload.logo_temp_location = logoTempLocation;
      if (regulatoryBodies) {
        if (Array.isArray(regulatoryBodies)) {
          payload.regulatory_registrations = regulatoryBodies;
        } else {
          payload.regulatory_bodies = regulatoryBodies;
        }
      }
      if (billingData) payload.billing_data = billingData;
      
      const response = await api.post('/entities/firms', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating firm:', error);
      throw error;
    }
  },

  /**
   * Get firm by manager ID (GET /entities/firms?manager_id=xxx)
   */
  getFirmByManagerId: async (managerId) => {
    try {
      const response = await api.get('/entities/firms', {
        params: { manager_id: managerId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching firm by manager ID:', error);
      throw error;
    }
  },

  /**
   * Update firm by ID (PUT /entities/firms)
   */
  updateFirmById: async (firmId, firmData) => {
    try {
      const payload = {
        firm_id: firmId,
        firm_data: firmData
      };
      
      const response = await api.put('/entities/firms', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating firm:', error);
      throw error;
    }
  },

  // === TEAM MEMBERS MANAGEMENT ===
  
  /**
   * Get all team members for a firm
   */
  getTeamMembers: async (firmId) => {
    try {
      const response = await api.get('/entities/team', {
        params: { firm_id: firmId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting team members:', error);
      throw error;
    }
  },

  /**
   * Get specific team member by member_id
   */
  getTeamMember: async (firmId, memberId) => {
    try {
      const response = await api.get('/entities/team', {
        params: { firm_id: firmId, member_id: memberId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting team member:', error);
      throw error;
    }
  },

  /**
   * Create new team member
   */
  createTeamMember: async (firmId, memberData, createdBy) => {
    try {
      const response = await api.post('/entities/team', {
        firm_id: firmId,
        member_data: memberData,
        created_by: createdBy
      });
      return response.data;
    } catch (error) {
      console.error('Error creating team member:', error);
      throw error;
    }
  },

  /**
   * Update existing team member
   */
  updateTeamMember: async (firmId, memberId, memberData) => {
    try {
      const response = await api.put('/entities/team', {
        firm_id: firmId,
        member_id: memberId,
        member_data: memberData
      });
      return response.data;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
  },

  /**
   * Update multiple team members (batch)
   */
  updateTeamMembers: async (firmId, membersArray) => {
    try {
      const response = await api.put('/entities/team', {
        firm_id: firmId,
        members: membersArray
      });
      return response.data;
    } catch (error) {
      console.error('Error updating team members:', error);
      throw error;
    }
  },

  /**
   * Delete team member (soft delete)
   */
  deleteTeamMember: async (firmId, memberId) => {
    try {
      const response = await api.delete('/entities/team', {
        data: {
          firm_id: firmId,
          member_id: memberId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting team member:', error);
      throw error;
    }
  },

  // === FUNDS MANAGEMENT ===
  
  /**
   * Get all funds for a firm
   */
  getFunds: async (firmId) => {
    try {
      const response = await api.get('/entities/funds', {
        params: { firm_id: firmId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting funds:', error);
      throw error;
    }
  },

  /**
   * Get specific fund by fund_id
   */
  getFund: async (fundId) => {
    try {
      const response = await api.get('/entities/funds', {
        params: { fund_id: fundId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting fund:', error);
      throw error;
    }
  },

  /**
   * Create new fund
   */
  createFund: async (firmId, fundData, createdBy) => {
    try {
      const response = await api.post('/entities/funds', {
        firm_id: firmId,
        fund_data: fundData,
        created_by: createdBy
      });
      return response.data;
    } catch (error) {
      console.error('Error creating fund:', error);
      throw error;
    }
  },

  /**
   * Update existing fund
   */
  updateFund: async (fundId, fundData) => {
    try {
      const response = await api.put('/entities/funds', {
        fund_id: fundId,
        fund_data: fundData
      });
      return response.data;
    } catch (error) {
      console.error('Error updating fund:', error);
      throw error;
    }
  },

  /**
   * Delete fund (hard delete)
   */
  deleteFund: async (fundId) => {
    try {
      const response = await api.delete('/entities/funds', {
        data: {
          fund_id: fundId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting fund:', error);
      throw error;
    }
  },

  // === USER MANAGEMENT ===
  
  /**
   * Get user profile by user_id
   */
  getUser: async (userId) => {
    try {
      const response = await api.get('/entities/user', {
        params: { user_id: userId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put('/entities/user', {
        user_id: userId,
        user_data: userData
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }
};

export default entityService;